import { X } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

import { getFormNameByWorkflowUser } from "@/api/form";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { cn } from "@/lib/utils";

import { useDashboardTabsStore } from "@/hooks/store/use-dashboard-tabs-store";

import { routeLabels } from "@/components/common/breadcrumbs/breadcrumb-labels";

const normalizePath = (path: string) =>
  path.endsWith("/") && path !== "/" ? path.slice(0, -1) : path;

const getDynamicRouteLabel = (path: string) => {
  const entries = Object.entries(routeLabels);

  for (const [pattern, label] of entries) {
    const regex = new RegExp("^" + pattern.replace(/:[^/]+/g, "[^/]+") + "$");
    if (regex.test(path)) return label;
  }

  const lastSegment = path.split("/").filter(Boolean).pop();

  // if (path === "/dashboard") return "داشبورد";

  return lastSegment
    ? lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1)
    : "داشبورد";
};

const getShortTabLabel = (label: string) => {
  const characters = Array.from(label);
  return characters.length > 20
    ? `${characters.slice(0, 18).join("")}...`
    : label;
};

const MAX_TABS = 6;
const MAX_VISIBLE_TABS = MAX_TABS * 2;

// const DASHBOARD_TAB = {
//   path: "/dashboard",
//   label: "داشبورد",
//   isPermanent: true
// };

const DashboardTabs = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { tabs, activeTabPath, openTab, closeTab, setActiveTab } =
    useDashboardTabsStore();

  // تشخیص صفحه entity با GUID
  const entityGuid = location.pathname.split("/").pop();
  // دریافت نام فرم از طریق workflowUserId
  const { data: formName } = useQuery({
    queryKey: ["formName", entityGuid],
    queryFn: () => getFormNameByWorkflowUser(entityGuid!), // تابع جدید از api/form
    enabled: !!entityGuid
  });

  // به‌روزرسانی لیبل تب فرم وقتی نام از API برگشت
  useEffect(() => {
    if (!entityGuid || !formName) return;

    const normalizedCurrentPath = normalizePath(location.pathname);

    const currentTabInStore = tabs.find(
      (tab) => normalizePath(tab.path) === normalizedCurrentPath
    );
    if (!currentTabInStore || currentTabInStore.label === formName) return;

    const updatedTabs = tabs.map((tab) =>
      normalizePath(tab.path) === normalizedCurrentPath
        ? { ...tab, label: formName }
        : tab
    );

    useDashboardTabsStore.setState({ tabs: updatedTabs });
  }, [formName, entityGuid, location.pathname, tabs]);

  const currentTab = useMemo(() => {
    const locationState = location.state as
      | { tabTitle?: string; tabPath?: string }
      | null
      | undefined;

    const tabPath = normalizePath(locationState?.tabPath ?? location.pathname);
    const tabLabel = locationState?.tabTitle ?? getDynamicRouteLabel(tabPath);

    return {
      path: tabPath,
      label: tabLabel,
      replaceLabel: Boolean(locationState?.tabTitle)
    };
  }, [location.pathname, location.state]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (currentTab.path === "/dashboard") {
      setActiveTab("/dashboard");
      return;
    }

    const handleTab = () => {
      const tabExists = tabs.some(
        (tab) => normalizePath(tab.path) === currentTab.path
      );

      if (!tabExists) {
        if (tabs.length >= MAX_VISIBLE_TABS) {
          // اولین تب غیر-داشبورد رو ببند
          const firstNonDashboard = tabs.find(
            (t) => normalizePath(t.path) !== "/dashboard"
          );
          if (firstNonDashboard) closeTab(firstNonDashboard.path);
        }
        openTab(currentTab, currentTab.replaceLabel);
      }

      setActiveTab(currentTab.path);
    };

    if (isInitialMount.current) {
      isInitialMount.current = false;
      handleTab();
      return;
    }

    handleTab();
  }, [currentTab, openTab, setActiveTab, closeTab, tabs]);

  // آپدیت لیبل تب فرم وقتی اسم فرم از API برگشت

  // cleanup تب‌های اضافی
  useEffect(() => {
    const nonDashboardTabs = tabs.filter(
      (t) => normalizePath(t.path) !== "/dashboard"
    );
    if (nonDashboardTabs.length <= MAX_VISIBLE_TABS) return;

    const firstNonDashboard = nonDashboardTabs[0];
    if (firstNonDashboard) closeTab(firstNonDashboard.path);
  }, [closeTab, tabs]);

  const filteredTabs = useMemo(() => {
    return tabs.filter(
      (tab) =>
        normalizePath(tab.path) !== "/dashboard" && tab.label !== "داشبورد"
    );
  }, [tabs]);

  const visibleTabs = useMemo(() => {
    return filteredTabs.slice(-MAX_VISIBLE_TABS);
  }, [filteredTabs]);

  // const isDashboardActive =
  //   normalizePath(activeTabPath) === "/dashboard" ||
  //   activeTabPath === "" ||
  //   location.pathname === "/dashboard";

  const renderTab = (tab: (typeof visibleTabs)[number]) => {
    const isActive = normalizePath(tab.path) === normalizePath(activeTabPath);
    // اگر این تب، تب جاری فرم باشد و اسم فرم موجود باشد، نمایش داده شود
    const isCurrentTab =
      normalizePath(tab.path) === normalizePath(location.pathname);
    const displayLabel = isCurrentTab && formName ? formName : tab.label;
    return (
      <button
        key={tab.path}
        type="button"
        onClick={() => {
          setActiveTab(tab.path);
          navigate(tab.path);
        }}
        className={cn(
          "flex min-w-36 items-center px-4 py-2 text-sm transition-all",
          isActive
            ? "rounded-bl-xl bg-white text-slate-900 shadow-[0_-1px_0_0_rgba(15,23,42,0.04)]"
            : "border-slate-200 bg-[#edf4f8] text-slate-600 hover:bg-white hover:text-slate-900"
        )}
      >
        <span className="font-medium text-gray-700">
          {getShortTabLabel(displayLabel)}
        </span>

        <span
          className="ms-2 inline-flex size-5 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-900"
          onClick={(event) => {
            event.stopPropagation();
            const removedTab = closeTab(tab.path);

            if (removedTab && isActive) {
              const fallbackTab =
                tabs.find(
                  (t) => normalizePath(t.path) !== normalizePath(tab.path)
                ) ?? null;

              if (fallbackTab) {
                setActiveTab(fallbackTab.path);
                navigate(fallbackTab.path);
              } else {
                setActiveTab("/dashboard");
                navigate("/dashboard");
              }
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`بستن تب ${tab.label}`}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              event.stopPropagation();
              const removedTab = closeTab(tab.path);

              if (removedTab && isActive) {
                const fallbackTab =
                  tabs.find(
                    (t) => normalizePath(t.path) !== normalizePath(tab.path)
                  ) ?? null;

                if (fallbackTab) {
                  setActiveTab(fallbackTab.path);
                  navigate(fallbackTab.path);
                } else {
                  setActiveTab("/dashboard");
                  navigate("/dashboard");
                }
              }
            }
          }}
        >
          <X size={12} />
        </span>
      </button>
    );
  };

  const topRowTabs = visibleTabs.slice(0, MAX_TABS);
  const bottomRowTabs = visibleTabs.slice(MAX_TABS, MAX_VISIBLE_TABS);

  return (
    <div
      className={cn(
        "z-30 px-3",
        visibleTabs.length > MAX_TABS ? "mt-10" : "mt-1"
      )}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center overflow-x-auto whitespace-nowrap">
          {/* تب داشبورد - همیشه نمایش داده می‌شه، بدون دکمه بستن */}
          {/* <button
            type="button"
            onClick={() => {
              setActiveTab("/dashboard");
              navigate("/dashboard");
            }}
            className={cn(
              "flex min-w-36 items-center px-4 py-2 text-sm transition-all",
              isDashboardActive
                ? "rounded-bl-xl bg-white text-slate-900 shadow-[0_-1px_0_0_rgba(15,23,42,0.04)]"
                : "border-slate-200 bg-[#edf4f8] text-slate-600 hover:bg-white hover:text-slate-900"
            )}
          >
            <span className="font-medium text-gray-700">
              {DASHBOARD_TAB.label}
            </span>
          </button> */}

          {topRowTabs.map(renderTab)}
        </div>

        {bottomRowTabs.length > 0 && (
          <div className="flex items-center overflow-x-auto whitespace-nowrap">
            {bottomRowTabs.map(renderTab)}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardTabs;
